import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useEffect, useState, type ReactElement} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {ModalDialog, ModalPanel} from "@khanacademy/wonder-blocks-modal";
import {SchedulePolicy, useTimeout} from "@khanacademy/wonder-blocks-timing";

// Modal takes up 95% of the viewport to provide some padding around the edges
// and facilitate the modal's click to dismiss option.
const MODAL_VIEWPORT_PERCENTAGE = 0.95;

// Padding offsets to account for modal chrome (borders, headers, etc.)
const IMAGE_WIDTH_OFFSET = 48;
const IMAGE_HEIGHT_OFFSET = 148;

// Debounce timeout for window resize events (in milliseconds)
const RESIZE_DEBOUNCE_MS = 50;

interface Props {
    // The base64 encoded image to display fullscreen
    image: string;
    // Optional alt-text for the image
    altText?: string;
    // Function to call when the modal should be closed
}

/**
 * Calculate image dimensions based on window size with appropriate padding
 */
function calculateImageDimensions() {
    return {
        width:
            window.innerWidth * MODAL_VIEWPORT_PERCENTAGE - IMAGE_WIDTH_OFFSET,
        height:
            window.innerHeight * MODAL_VIEWPORT_PERCENTAGE -
            IMAGE_HEIGHT_OFFSET,
    };
}

/**
 * Displays an image in fullscreen mode as a dismissable modal.
 */
export function FullscreenImageModal({image, altText}: Props): ReactElement {
    // State to track image dimensions that update on window resize
    const [imageDimensions, setImageDimensions] = useState(
        calculateImageDimensions,
    );

    // Debounced timeout for window resize
    const {set: scheduleResize, clear: clearResize} = useTimeout(
        () => {
            setImageDimensions(calculateImageDimensions());
        },
        RESIZE_DEBOUNCE_MS,
        {schedulePolicy: SchedulePolicy.OnDemand},
    );

    useEffect(() => {
        const handleResize = () => {
            clearResize();
            scheduleResize();
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            clearResize();
        };
    }, [scheduleResize, clearResize]);

    return (
        <ModalDialog
            style={styles.modalDialog}
            aria-labelledby="fullscreen-image-modal"
        >
            <ModalPanel
                closeButtonVisible={true}
                content={
                    <View style={styles.imageContainer}>
                        <img
                            data-testid="fullscreen-image-modal"
                            src={image}
                            height={imageDimensions.height}
                            width={imageDimensions.width}
                            style={{objectFit: "contain"}}
                            alt={
                                altText
                                    ? t`Full size image: ${altText}`
                                    : t`Full size image`
                            }
                        />
                    </View>
                }
            />
        </ModalDialog>
    );
}

const styles = StyleSheet.create({
    modalDialog: {
        display: "block",
        width: `${MODAL_VIEWPORT_PERCENTAGE * 100}vw`,
        height: `${MODAL_VIEWPORT_PERCENTAGE * 100}vh`,
        padding: 0,
        margin: 0,
    },
    imageContainer: {
        // Offset for height of close button/header
        paddingBlockStart: 40,
    },
});
